import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

export const voiceReceptionistSchema = z.object({
  /** Rolled out to this clinic; the controls stay hidden otherwise. */
  visible: z.boolean(),
  enabled: z.boolean(),
  outboundAvailable: z.boolean(),
  testCallAvailable: z.boolean().default(false),
  number: z
    .object({
      phoneNumber: z.string(),
      assignment: z.enum(['dedicated', 'shared']),
    })
    .nullable(),
  handoffNumber: z.string().nullable(),
  clinicLines: z.array(z.string()),
  recordCalls: z.boolean(),
  country: z.string().default('IE'),
  voiceId: z.string().nullable().default(null),
  defaultVoiceId: z.string().default('3b8fXc91YHS1i2DYAlBQ'),
});
export type VoiceReceptionist = z.infer<typeof voiceReceptionistSchema>;

export interface UpdateVoiceReceptionistInput {
  enabled?: boolean;
  handoffNumber?: string | null;
  clinicLines?: string[];
  recordCalls?: boolean;
  voiceId?: string | null;
}

const voiceChoiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  accent: z.string(),
  region: z.string().nullable().optional(),
  previewUrl: z.string().url().nullable(),
});

/** What the clinic changed, as intent; the hook builds the request body. */
export type VoiceReceptionistIntent =
  | { kind: 'enabled'; enabled: boolean }
  | { kind: 'recordCalls'; recordCalls: boolean }
  | { kind: 'settings'; handoffNumber: string | null; voiceId: string | null };

export function voiceReceptionistPayload(
  intent: VoiceReceptionistIntent
): UpdateVoiceReceptionistInput {
  switch (intent.kind) {
    case 'enabled':
      return { enabled: intent.enabled };
    case 'recordCalls':
      return { recordCalls: intent.recordCalls };
    case 'settings': {
      return {
        handoffNumber: intent.handoffNumber,
        voiceId: intent.voiceId,
      };
    }
  }
}

export function useVoiceReceptionist() {
  const client = useQueryClient();
  const queryKey = queryKeys.voiceReceptionist.settings();
  const query = useQuery({
    queryKey,
    queryFn: () =>
      apiClient.get('voice-receptionist/settings', {
        schema: voiceReceptionistSchema,
      }),
  });
  const mutation = useMutation({
    mutationFn: (intent: VoiceReceptionistIntent) =>
      apiClient.put(
        'voice-receptionist/settings',
        voiceReceptionistPayload(intent),
        { schema: voiceReceptionistSchema }
      ),
    onSuccess: (settings, intent) => {
      client.setQueryData(queryKey, settings);
      toast.success(
        intent.kind !== 'enabled'
          ? 'Phone receptionist saved'
          : settings.enabled
            ? 'Phone receptionist enabled'
            : 'Phone receptionist disabled'
      );
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Could not update the phone receptionist'),
  });
  const voicesQuery = useQuery({
    queryKey: queryKeys.voiceReceptionist.voices(),
    enabled: query.data?.visible === true,
    staleTime: 300_000,
    retry: 1,
    queryFn: () =>
      apiClient.get('voice-receptionist/voices', {
        schema: z.array(voiceChoiceSchema),
      }),
  });
  const save = (intent: VoiceReceptionistIntent) => mutation.mutate(intent);
  const previewVoice = (voiceId: string) =>
    client.fetchQuery({
      queryKey: queryKeys.voiceReceptionist.preview(voiceId),
      staleTime: 900_000,
      queryFn: () =>
        apiClient.post(
          `voice-receptionist/voices/${encodeURIComponent(voiceId)}/preview`,
          {},
          { schema: z.object({ audio: z.string().min(1) }) }
        ),
    });
  return {
    query,
    mutation,
    voicesQuery,
    previewVoice,
    setEnabled: (enabled: boolean) => save({ kind: 'enabled', enabled }),
    setRecordCalls: (recordCalls: boolean) =>
      save({ kind: 'recordCalls', recordCalls }),
    saveSettings: (handoffNumber: string | null, voiceId: string | null) =>
      save({ kind: 'settings', handoffNumber, voiceId }),
  };
}
